// Riconoscimento della modalità di immissione di un campo (issue #3594, feature 144): distingue la
// lettura di un lettore barcode keyboard-wedge (raffica rapida di tasti, oppure testo inserito in un
// colpo dai lettori integrati Android) dalla digitazione a mano e dai valori impostati dal codice.
// Classe pura con tempo iniettato: nessun listener globale, nessun timer.

// Stesse soglie del lettore badge operatori (BadgeReaderService.interKeyThresholdMs/minCodeLength).
export const WEDGE_SOGLIA_MS = 50;
export const WEDGE_MIN_CARATTERI = 3;

// 'nessuna': nulla registrato dall'ultimo reset (valore impostato dall'applicazione).
export type ModalitaImmissione = 'nessuna' | 'scansione' | 'manuale';

const TASTI_IGNORATI = new Set(['Unidentified', 'Process']);
const TASTI_CORREZIONE = new Set(['Backspace', 'Delete']);
const INSERIMENTI_IN_BLOCCO = new Set(['insertText', 'insertReplacementText']);

export class KeyboardWedgeBurst {
  private buffer = '';
  private ultimoTasto: number | null = null;
  // Tasti stampabili già contati dal keydown, in attesa del loro evento input.
  private tastiInAttesa = 0;
  private immissione = false;
  private manuale = false;

  constructor(private readonly sogliaMs = WEDGE_SOGLIA_MS,
              private readonly minCaratteri = WEDGE_MIN_CARATTERI) {
  }

  registraTasto(key: string, now: number, modificatore = false): void {
    if (TASTI_IGNORATI.has(key)) return;
    if (TASTI_CORREZIONE.has(key) || (modificatore && key.length === 1)) {
      this.marcaManuale();
      return;
    }
    if (key.length !== 1) return; // Enter, Tab, Shift, frecce…
    this.immissione = true;
    if (this.ultimoTasto !== null && now - this.ultimoTasto > this.sogliaMs) this.manuale = true;
    this.ultimoTasto = now;
    this.buffer += key;
    this.tastiInAttesa++;
  }

  registraInput(inputType: string | null | undefined, data: string | null | undefined): void {
    if (!inputType) return; // resta il keydown
    const testo = data ?? '';
    if (!INSERIMENTI_IN_BLOCCO.has(inputType)) {
      this.marcaManuale(); // incolla, trascina, composizione IME, delete…
      return;
    }
    if (testo.length >= this.minCaratteri) {
      this.immissione = true;
      this.buffer += testo;
      return;
    }
    this.consumaTastiInAttesa(testo.length);
  }

  marcaManuale(): void {
    this.immissione = true;
    this.manuale = true;
  }

  classifica(valore: string): ModalitaImmissione {
    if (!this.immissione) return 'nessuna';
    const lunghezza = (valore ?? '').length;
    const scansione = !this.manuale && this.buffer.length >= this.minCaratteri && this.buffer.length === lunghezza;
    return scansione ? 'scansione' : 'manuale';
  }

  reset(): void {
    this.buffer = '';
    this.ultimoTasto = null;
    this.tastiInAttesa = 0;
    this.immissione = false;
    this.manuale = false;
  }

  // insertText di 1–2 caratteri: già contato se preceduto dal keydown stampabile corrispondente,
  // altrimenti è la tastiera virtuale (keydown Unidentified/229) → digitazione a mano.
  private consumaTastiInAttesa(caratteri: number): void {
    if (caratteri > 0 && this.tastiInAttesa >= caratteri) {
      this.tastiInAttesa -= caratteri;
      return;
    }
    this.marcaManuale();
  }
}
